import React, { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import SplashScreen from './components/SplashScreen';
import Layout from './components/Layout';
import { MusicContext } from './components/MusicContext';
import './App.css';

const HomePage = lazy(() => import('./pages/HomePage'));
const MoviePage = lazy(() => import('./pages/MoviePage'));
const ArtPage = lazy(() => import('./pages/ArtPage'));
const ArtistPage = lazy(() => import('./pages/ArtistPage'));
const StorybookPage = lazy(() => import('./pages/StorybookPage'));

function SplashScreenWrapper({ hasEntered, onEnter, onPlayMusic, isContentLoaded }) {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (hasEntered) {
      const timer = setTimeout(() => setIsVisible(false), 1100);
      return () => clearTimeout(timer);
    }
  }, [hasEntered]);

  if (!isHomePage || !isVisible) return null;
  return <SplashScreen onEnter={onEnter} onPlayMusic={onPlayMusic} isContentLoaded={isContentLoaded} />;
}

function AppContent() {
  const location = useLocation();
  const [hasEntered, setHasEntered] = useState(false);
  const [isContentLoaded, setIsContentLoaded] = useState(false);
  const [isEndcreditsPlaying, setIsEndcreditsPlaying] = useState(false);
  const ambienceRef = useRef(null);
  const endcreditsRef = useRef(null);
  const ambienceStarted = useRef(false);
  const videoRef = useRef(null);

  // Audio setup
  useEffect(() => {
    const ambience = new Audio(process.env.PUBLIC_URL + '/ambience.mp3');
    ambience.loop = true;
    ambienceRef.current = ambience;

    const endcredits = new Audio(process.env.PUBLIC_URL + '/endcredits.mp3');
    endcredits.loop = true;
    endcreditsRef.current = endcredits;

    // Restore positions
    const savedAmbience = localStorage.getItem('ambienceTime');
    if (savedAmbience) ambience.currentTime = parseFloat(savedAmbience);
    const savedEndcredits = localStorage.getItem('endcreditsTime');
    if (savedEndcredits) endcredits.currentTime = parseFloat(savedEndcredits);

    // Save positions periodically
    let lastSave = 0;
    const savePositions = () => {
      const now = Date.now();
      if (now - lastSave > 3000) {
        localStorage.setItem('ambienceTime', String(ambience.currentTime));
        localStorage.setItem('endcreditsTime', String(endcredits.currentTime));
        lastSave = now;
      }
    };

    const saveNow = () => {
      localStorage.setItem('ambienceTime', String(ambience.currentTime));
      localStorage.setItem('endcreditsTime', String(endcredits.currentTime));
    };

    ambience.addEventListener('timeupdate', savePositions);
    endcredits.addEventListener('timeupdate', savePositions);
    ambience.addEventListener('pause', saveNow);
    endcredits.addEventListener('pause', saveNow);
    window.addEventListener('beforeunload', saveNow);

    return () => {
      ambience.removeEventListener('timeupdate', savePositions);
      endcredits.removeEventListener('timeupdate', savePositions);
      ambience.removeEventListener('pause', saveNow);
      endcredits.removeEventListener('pause', saveNow);
      window.removeEventListener('beforeunload', saveNow);
      ambience.pause();
      endcredits.pause();
    };
  }, []);

  // Audio on route change
  useEffect(() => {
    const ambience = ambienceRef.current;
    const endcredits = endcreditsRef.current;
    if (!ambience || !endcredits) return;

    if (location.pathname === '/') {
      endcredits.pause();
      setIsEndcreditsPlaying(false);
      if (ambienceStarted.current) {
        ambience.play().catch(() => {});
      }
    } else if (location.pathname === '/art') {
      ambience.pause();
      endcredits.volume = 0;
      endcredits.play()
        .then(() => {
          setIsEndcreditsPlaying(true);
          const fadeIn = setInterval(() => {
            if (!endcreditsRef.current) return clearInterval(fadeIn);
            const next = Math.min(1, endcreditsRef.current.volume + 0.05);
            endcreditsRef.current.volume = next;
            if (next >= 1) clearInterval(fadeIn);
          }, 100);
        })
        .catch(() => {});
    } else {
      ambience.pause();
      endcredits.pause();
      setIsEndcreditsPlaying(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (document.readyState === 'complete') {
      setIsContentLoaded(true);
    } else {
      const handleLoad = () => {
        setTimeout(() => setIsContentLoaded(true), 1000);
      };
      window.addEventListener('load', handleLoad);
      return () => window.removeEventListener('load', handleLoad);
    }
  }, []);

  const registerVideo = useCallback((el) => {
    videoRef.current = el;
  }, []);

  // Play audio on splash enter
  const playAmbience = useCallback(() => {
    if (ambienceRef.current) {
      ambienceStarted.current = true;
      ambienceRef.current.volume = 0;
      ambienceRef.current.play().catch(() => {});
      const target = 1;
      const step = 0.05;
      const interval = setInterval(() => {
        if (!ambienceRef.current) return clearInterval(interval);
        const next = Math.min(target, ambienceRef.current.volume + step);
        ambienceRef.current.volume = next;
        if (next >= target) clearInterval(interval);
      }, 100);
    }
    if (videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  }, []);

  const pauseAmbience = useCallback(() => {
    if (!ambienceRef.current) return;
    const fadeOut = setInterval(() => {
      if (!ambienceRef.current) return clearInterval(fadeOut);
      const next = Math.max(0, ambienceRef.current.volume - 0.05);
      ambienceRef.current.volume = next;
      if (next <= 0) {
        clearInterval(fadeOut);
        ambienceRef.current.pause();
      }
    }, 100);
  }, []);

  const resumeAmbience = useCallback(() => {
    if (!ambienceRef.current) return;
    ambienceRef.current.volume = 0;
    ambienceRef.current.play().catch(() => {});
    const fadeIn = setInterval(() => {
      if (!ambienceRef.current) return clearInterval(fadeIn);
      const next = Math.min(1, ambienceRef.current.volume + 0.05);
      ambienceRef.current.volume = next;
      if (next >= 1) clearInterval(fadeIn);
    }, 100);
  }, []);

  // Art page toggle for endcredits
  const toggleEndcredits = useCallback(() => {
    if (!endcreditsRef.current) return;
    if (endcreditsRef.current.paused) {
      endcreditsRef.current.play()
        .then(() => setIsEndcreditsPlaying(true))
        .catch(() => {});
    } else {
      endcreditsRef.current.pause();
      setIsEndcreditsPlaying(false);
    }
  }, []);

  const handleEnter = () => {
    setHasEntered(true);
  };

  return (
    <MusicContext.Provider value={{ isPlaying: isEndcreditsPlaying, toggleMusic: toggleEndcredits, registerVideo, pauseAmbience, resumeAmbience, hasEntered }}>
      <Suspense fallback={<div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>}>
        <Routes>
          <Route path='/' element={<Layout />}>
            <Route index element={<HomePage />} />
            <Route path='/movie' element={<MoviePage />} />
            <Route path='/art' element={<ArtPage />} />
            <Route path='/team' element={<ArtistPage />} />
            <Route path='/storybook' element={<StorybookPage />} />
          </Route>
        </Routes>
      </Suspense>
      <SplashScreenWrapper
        hasEntered={hasEntered}
        onEnter={handleEnter}
        onPlayMusic={playAmbience}
        isContentLoaded={isContentLoaded}
      />
    </MusicContext.Provider>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
